/**
 * Data Source Manager
 * Abstracts different data sources for the Process Visualization Widget
 */
export class DataSourceManager {
    constructor(config) {
        this.config = config;
        this.dataSources = {
            api: new APIDataSource(config.dataSource.api),
            static: new StaticDataSource(config.dataSource.static),
            callback: new CallbackDataSource(config.dataSource.callback)
        };
    }

    /**
     * Fetch data from the configured data source
     */
    async fetchData() {
        const sourceType = this.config.dataSource.type;
        
        if (!this.dataSources[sourceType]) {
            throw new Error(`Unknown data source type: ${sourceType}`);
        }

        return await this.dataSources[sourceType].fetch();
    }

    /**
     * Update data source configuration
     */
    updateConfig(newConfig) {
        this.config = newConfig;
        
        // Update existing data sources with new config
        this.dataSources.api.updateConfig(newConfig.dataSource.api);
        this.dataSources.static.updateConfig(newConfig.dataSource.static);
        this.dataSources.callback.updateConfig(newConfig.dataSource.callback);
    }

    /**
     * Test connectivity for the current data source
     */
    async testConnection() {
        const sourceType = this.config.dataSource.type;
        
        if (this.dataSources[sourceType].testConnection) {
            return await this.dataSources[sourceType].testConnection();
        }
        
        return true; // Default to true for sources that don't support testing
    }
}

/**
 * API Data Source
 * Handles fetching data from REST API endpoints
 */
export class APIDataSource {
    constructor(config) {
        this.config = config;
    }

    async fetch() {
        const url = `${this.config.baseUrl}${this.config.endpoint}?db_source=${this.config.dbSource}`;
        
        try {
            const response = await fetch(url, {
                method: 'GET',
                headers: this.config.headers
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status} - ${response.statusText}`);
            }

            const data = await response.json();
            
            // Validate the response structure
            this.validateApiResponse(data);
            
            return data;
        } catch (error) {
            if (error.name === 'TypeError' && error.message.includes('fetch')) {
                throw new Error('Network error: Unable to connect to the API server. Please check if the server is running.');
            }
            throw error;
        }
    }

    /**
     * Validate that the API response has the expected structure
     */
    validateApiResponse(data) {
        if (!data || typeof data !== 'object') {
            throw new Error('Invalid API response: Expected an object');
        }

        if (!data.edges || !Array.isArray(data.edges)) {
            throw new Error('Invalid API response: Missing or invalid "edges" array');
        }

        if (!data.nodes || typeof data.nodes !== 'object') {
            throw new Error('Invalid API response: Missing or invalid "nodes" object');
        }

        if (!data.process_start || typeof data.process_start !== 'string') {
            throw new Error('Invalid API response: Missing or invalid "process_start" field');
        }

        if (!data.process_end || typeof data.process_end !== 'string') {
            throw new Error('Invalid API response: Missing or invalid "process_end" field');
        }

        // Validate edge structure
        data.edges.forEach((edge, index) => {
            if (!edge.source || !edge.target || typeof edge.count !== 'number') {
                throw new Error(`Invalid edge at index ${index}: Missing required fields (source, target, count)`);
            }
            if (typeof edge.avg_duration !== 'number') {
                throw new Error(`Invalid edge at index ${index}: Missing or invalid avg_duration field`);
            }
        });

        // Validate node structure
        Object.entries(data.nodes).forEach(([nodeId, nodeData]) => {
            if (!nodeData.label || typeof nodeData.count !== 'number') {
                throw new Error(`Invalid node "${nodeId}": Missing required fields (label, count)`);
            }
        });
    }

    async testConnection() {
        try {
            await this.fetch();
            return true;
        } catch (error) {
            console.error('API connection test failed:', error);
            return false;
        }
    }

    updateConfig(newConfig) {
        this.config = { ...this.config, ...newConfig };
    }
}

/**
 * Static Data Source
 * Handles static data injection
 */
export class StaticDataSource {
    constructor(config) {
        this.config = config;
    }

    async fetch() {
        if (!this.config.data) {
            throw new Error('No static data provided');
        }

        // Validate the static data structure
        this.validateStaticData(this.config.data);
        
        return this.config.data;
    }

    validateStaticData(data) {
        if (!data || typeof data !== 'object') {
            throw new Error('Invalid static data: Expected an object');
        }

        if (!data.edges || !Array.isArray(data.edges)) {
            throw new Error('Invalid static data: Missing or invalid "edges" array');
        }

        if (!data.nodes || typeof data.nodes !== 'object') {
            throw new Error('Invalid static data: Missing or invalid "nodes" object');
        }

        if (!data.process_start || typeof data.process_start !== 'string') {
            throw new Error('Invalid static data: Missing or invalid "process_start" field');
        }

        if (!data.process_end || typeof data.process_end !== 'string') {
            throw new Error('Invalid static data: Missing or invalid "process_end" field');
        }
    }

    updateConfig(newConfig) {
        this.config = { ...this.config, ...newConfig };
    }
}

/**
 * Callback Data Source
 * Handles data fetching through user-provided callback functions
 */
export class CallbackDataSource {
    constructor(config) {
        this.config = config;
    }

    async fetch() {
        if (!this.config.fetchFunction || typeof this.config.fetchFunction !== 'function') {
            throw new Error('No fetch function provided for callback data source');
        }

        try {
            const data = await this.config.fetchFunction();
            
            // Validate the callback data structure
            this.validateCallbackData(data);
            
            return data;
        } catch (error) {
            throw new Error(`Callback data source error: ${error.message}`);
        }
    }

    validateCallbackData(data) {
        if (!data || typeof data !== 'object') {
            throw new Error('Invalid callback data: Expected an object');
        }

        if (!data.edges || !Array.isArray(data.edges)) {
            throw new Error('Invalid callback data: Missing or invalid "edges" array');
        }

        if (!data.nodes || typeof data.nodes !== 'object') {
            throw new Error('Invalid callback data: Missing or invalid "nodes" object');
        }

        if (!data.process_start || typeof data.process_start !== 'string') {
            throw new Error('Invalid callback data: Missing or invalid "process_start" field');
        }

        if (!data.process_end || typeof data.process_end !== 'string') {
            throw new Error('Invalid callback data: Missing or invalid "process_end" field');
        }
    }

    updateConfig(newConfig) {
        this.config = { ...this.config, ...newConfig };
    }
}